import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PiArrowsClockwise, PiCalculator, PiPlus } from 'react-icons/pi';
import BottomSheet from '../BottomSheet';
import CalculatorModal from '../CalculatorModal';
import CategoryFormSheet from '../CategoryFormSheet';
import { useCategories } from '../../hooks/useCategories';
import { DateField, MoneyField, SelectField, TextField } from '../FormFields';
import MemberAttributionSelect from '../MemberAttributionSelect';
import {
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  WRITE_PAYLOAD_ERRORS,
  buildTransactionWritePayload,
  formatCentsForInput,
  getAmountCents,
  getFinancialDate,
  isValidTransactionType,
} from '../../domain/finance';
import {
  NO_RECURRENCE,
  RECURRENCE_END_NEVER,
  RECURRENCE_END_ON_DATE,
  RECURRENCE_OPTIONS,
  buildRecurrenceFormInput,
  getRecurrencePreview,
  getRecurrenceValueForSchedule,
} from '../AddItemModal/recurrenceOptions';
import './style.css';
import { getMemberLabel, humanMemberName } from '../../domain/finance/memberAttribution';

const EditItemModal = ({ item, show, onClose, onSave, accountId, currentRole, members = [], recurrenceContext = null }) => {
  const [nome, setNome] = useState(item.nome);
  const [valor, setValor] = useState(formatCentsForInput(getAmountCents(item)));
  const [categoria, setCategoria] = useState(item.categoria);
  const [data, setData] = useState(getFinancialDate(item) || '');
  const [status, setStatus] = useState(item.status);
  const [tipo] = useState(isValidTransactionType(item.tipo) ? item.tipo : TRANSACTION_TYPES.INCOME);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const { expenseCategories, incomeCategories, createCategory } = useCategories(accountId);
  const selectableCategories = tipo === TRANSACTION_TYPES.INCOME ? incomeCategories : expenseCategories;
  const canManageCategories = ['owner', 'admin', 'editor'].includes(currentRole);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [attributedToUid, setAttributedToUid] = useState(item.attributedToUid || '');
  const attributionOptions = [...members];
  if (item.attributedToUid && !attributionOptions.some((member) => (member.uid || member.id) === item.attributedToUid)) {
    attributionOptions.push({ uid: item.attributedToUid, displayName: humanMemberName(item.attributedToNameSnapshot, 'Membro removido') });
  }
  const selectedMember = attributionOptions.find((member) => (member.uid || member.id) === attributedToUid);
  const isFollowingScope = recurrenceContext?.scope === 'following';
  const initialSchedule = recurrenceContext?.segment?.schedule;
  const [repeat, setRepeat] = useState(() => getRecurrenceValueForSchedule(initialSchedule));
  const [recurrenceEnd, setRecurrenceEnd] = useState(() => (
    initialSchedule?.untilDate ? RECURRENCE_END_ON_DATE : RECURRENCE_END_NEVER
  ));
  const [untilDate, setUntilDate] = useState(initialSchedule?.untilDate || '');
  const recurrencePreview = isFollowingScope ? getRecurrencePreview({
    repeat, anchorDate: data, endMode: recurrenceEnd, untilDate,
  }) : '';

  const clearFieldError = (field) => {
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    if (saving) return;
    const result = buildTransactionWritePayload({
      nome,
      amountInput: valor,
      date: data,
      categoria,
      tipo,
      status,
    });

    const recurrenceResult = isFollowingScope
      ? buildRecurrenceFormInput({ repeat, anchorDate: data, endMode: recurrenceEnd, untilDate })
      : { ok: true, recurrence: null, errors: {} };
    const nextErrors = {};
    if (result.errors.includes(WRITE_PAYLOAD_ERRORS.INVALID_AMOUNT)) {
      nextErrors.valor = 'Informe um valor válido, com no máximo duas casas decimais.';
    }
    if (result.errors.includes(WRITE_PAYLOAD_ERRORS.INVALID_DATE)) {
      nextErrors.data = 'Informe uma data válida.';
    }
    if (result.errors.includes(WRITE_PAYLOAD_ERRORS.INVALID_STATUS)) {
      nextErrors.status = 'Selecione um status compatível com o tipo.';
    }
    if (recurrenceResult.errors?.repeat) nextErrors.repeat = recurrenceResult.errors.repeat;
    if (recurrenceResult.errors?.anchorDate) nextErrors.data = recurrenceResult.errors.anchorDate;
    if (recurrenceResult.errors?.endMode) nextErrors.recurrenceEnd = recurrenceResult.errors.endMode;
    if (recurrenceResult.errors?.untilDate) nextErrors.untilDate = recurrenceResult.errors.untilDate;
    setFieldErrors(nextErrors);

    if (!result.ok || !recurrenceResult.ok || (isFollowingScope && repeat === NO_RECURRENCE)) return;

    setSaving(true);
    try {
      const nextTransaction = { ...item, ...result.payload,
        attributedToUid: selectedMember ? attributedToUid : null,
        attributedToNameSnapshot: selectedMember ? getMemberLabel(selectedMember) : null,
      };
      await onSave(isFollowingScope
        ? { transaction: nextTransaction, recurrence: recurrenceResult.recurrence }
        : nextTransaction);

      onClose();
    } catch (error) {
      /*
       * A Dashboard já exibiu o feedback global.
       * Mantemos o modal aberto para o usuário
       * tentar novamente.
       */
      console.error(
        'Erro ao salvar edição:',
        error
      );
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <BottomSheet
        title="Editar movimentação"
        onClose={onClose}
        size="lg"
        headerActions={(
          <button type="button" className="nf-sheet__action" onClick={() => setShowCalculator(true)} aria-label="Abrir calculadora">
            <PiCalculator size={20} aria-hidden="true" />
          </button>
        )}
        footer={(
          <>
            <button type="submit" form="edit-transaction-form" className="nf-sheet__primary" disabled={saving}>{saving ? 'Salvando…' : 'Salvar'}</button>
            <button type="button" className="nf-sheet__secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          </>
        )}
      >
        {recurrenceContext && (
          <div className="transaction-sheet-form__recurrence-context" role="note">
            <PiArrowsClockwise aria-hidden="true" />
            <span><strong>Parte de uma recorrência</strong><small>{isFollowingScope ? 'Alterando esta e as próximas ocorrências.' : 'Alterando somente esta ocorrência.'}</small></span>
          </div>
        )}
        <form id="edit-transaction-form" className="transaction-sheet-form" onSubmit={handleSave}>
          <TextField id="edit-transaction-name" label="Nome" value={nome} onChange={setNome} placeholder="Ex.: Salário" />
          <MoneyField
            id="edit-transaction-amount"
            label="Valor"
            value={valor}
            onChange={(value) => { setValor(value); clearFieldError('valor'); }}
            error={fieldErrors.valor}
            required
          />
          <SelectField id="edit-transaction-category" label="Categoria" value={categoria} onChange={setCategoria}>
            <option value="">Selecione uma categoria</option>
            {!selectableCategories.some((category) => category.name === categoria) && categoria && <option value={categoria}>{categoria}</option>}
            {selectableCategories.map((category) => <option value={category.name} key={category.id}>{category.name}</option>)}
          </SelectField>
          {canManageCategories && (
            <button type="button" className="transaction-composer__new-category" onClick={() => setShowCategoryForm(true)}>
              <PiPlus aria-hidden="true" /> Nova categoria
            </button>
          )}
          <MemberAttributionSelect id="edit-transaction-attribution" label="Atribuir a" members={attributionOptions} value={attributedToUid} onChange={setAttributedToUid} />
          <DateField
            id="edit-transaction-date"
            label={isFollowingScope ? 'Data inicial da nova fase' : 'Data'}
            value={data}
            onChange={(value) => { setData(value); clearFieldError('data'); }}
            error={fieldErrors.data}
            required
          />
          {isFollowingScope && (
            <>
              <SelectField id="edit-transaction-repeat" label="Repetir" value={repeat} onChange={(value) => { setRepeat(value); clearFieldError('repeat'); }} error={fieldErrors.repeat}>
                {RECURRENCE_OPTIONS.filter((option) => option.value !== NO_RECURRENCE).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </SelectField>
              <div className="transaction-composer__recurrence">
                <SelectField id="edit-transaction-recurrence-end" label="Termina" value={recurrenceEnd} onChange={(value) => { setRecurrenceEnd(value); clearFieldError('recurrenceEnd'); if (value === RECURRENCE_END_NEVER) setUntilDate(''); }} error={fieldErrors.recurrenceEnd}>
                  <option value={RECURRENCE_END_NEVER}>Nunca</option>
                  <option value={RECURRENCE_END_ON_DATE}>Em uma data</option>
                </SelectField>
                {recurrenceEnd !== RECURRENCE_END_NEVER && (
                  <DateField id="edit-transaction-until-date" label="Data final" value={untilDate} onChange={(value) => { setUntilDate(value); clearFieldError('untilDate'); }} error={fieldErrors.untilDate} required />
                )}
                {recurrencePreview && <p className="transaction-composer__recurrence-preview">{recurrencePreview}</p>}
              </div>
            </>
          )}
          <SelectField
            id="edit-transaction-status"
            label="Status"
            value={status}
            onChange={(value) => { setStatus(value); clearFieldError('status'); }}
            error={fieldErrors.status}
            required
          >
            <option value={tipo === TRANSACTION_TYPES.INCOME ? TRANSACTION_STATUS.RECEIVED : TRANSACTION_STATUS.PAID}>
              {tipo === TRANSACTION_TYPES.INCOME ? TRANSACTION_STATUS.RECEIVED : TRANSACTION_STATUS.PAID}
            </option>
            <option value={TRANSACTION_STATUS.PENDING}>{TRANSACTION_STATUS.PENDING}</option>
          </SelectField>
        </form>
      </BottomSheet>
      {showCategoryForm && (
        <CategoryFormSheet
          onClose={() => setShowCategoryForm(false)}
          onSave={async (input) => { const created = await createCategory(input); setCategoria(created.name); }}
        />
      )}
      {showCalculator && (
        <CalculatorModal
          onClose={() => setShowCalculator(false)}
          onUseValue={(value) => {
            setValor(value);
            clearFieldError('valor');
          }}
        />
      )}
    </>
  );
};

EditItemModal.propTypes = {
  item: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  accountId: PropTypes.string,
  currentRole: PropTypes.string,
  members: PropTypes.array,
  recurrenceContext: PropTypes.object,
};

export default EditItemModal;
